import { ScrollArea as ScrollAreaPrimitive } from '@base-ui/react/scroll-area'
import * as React from 'react'

import { cn } from '#lib/utils'

function ScrollArea({
	className,
	children,
	viewportRef,
	orientation = 'vertical',
	focusableViewport = true,
	...props
}: ScrollAreaPrimitive.Root.Props & {
	viewportRef?: React.Ref<HTMLDivElement>
	orientation?: 'vertical' | 'horizontal'
	/**
	 * Base UI puts a scrollable viewport in the tab order, so a keyboard can
	 * reach a region with nothing focusable in it. Inside a `table` or a
	 * `rowgroup` that makes it a focusable generic element between the table
	 * and its rows, which ARIA forbids (`aria-required-children`, OPSSTD-6983);
	 * there the rows carry their own controls, so `false` leaves it out.
	 */
	focusableViewport?: boolean
}) {
	return (
		<ScrollAreaPrimitive.Root
			className={cn('relative', className)}
			data-slot='scroll-area'
			{...props}
		>
			<ScrollAreaPrimitive.Viewport
				className='size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1'
				data-slot='scroll-area-viewport'
				ref={viewportRef}
				// An explicit `undefined`, which Base UI's merge takes over its
				// own `tabIndex` and React then leaves off the element.
				{...(focusableViewport ? {} : { tabIndex: undefined })}
			>
				{children}
			</ScrollAreaPrimitive.Viewport>
			<ScrollBar orientation={orientation} />
			<ScrollAreaPrimitive.Corner />
		</ScrollAreaPrimitive.Root>
	)
}

function ScrollBar({
	className,
	orientation = 'vertical',
	...props
}: ScrollAreaPrimitive.Scrollbar.Props) {
	return (
		<ScrollAreaPrimitive.Scrollbar
			className={cn(
				'flex touch-none p-px transition-colors select-none data-horizontal:h-2.5 data-horizontal:flex-col data-horizontal:border-t data-horizontal:border-t-transparent data-vertical:h-full data-vertical:w-2.5 data-vertical:border-l data-vertical:border-l-transparent',
				className
			)}
			data-orientation={orientation}
			data-slot='scroll-area-scrollbar'
			orientation={orientation}
			{...props}
		>
			<ScrollAreaPrimitive.Thumb
				className='relative flex-1 rounded-full bg-border'
				data-slot='scroll-area-thumb'
			/>
		</ScrollAreaPrimitive.Scrollbar>
	)
}

export { ScrollArea, ScrollBar }
